import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ErrorHandler from "../Components/ErrorHandler";
import { tableContainerStyle, tableRowStyle } from "../constents";
import { Link } from "react-router-dom";
import { useMyContext } from "../context";

const TABLE_HEADER_I18N = {
  actions: "common.actions",
  action: "common.actions",
  export: "common.export",
  status: "common.status",
  name: "admin.name",
  phone: "admin.phone",
  email: "admin.email",
  type: "admin.type",
  "date-time": "admin.dateTime",
  "date & time": "admin.dateAndTime",
  user: "admin.user",
  role: "admin.role",
  description: "admin.description",
  "source type": "admin.sourceType",
  "source id": "admin.sourceId",
  "points delta": "admin.pointsDelta",
  "balance after": "admin.balanceAfter",
  "user id": "admin.userId",
  "profile picture": "admin.profilePicture",
  "full name": "admin.fullName",
  "phone number": "admin.phoneNumber",
  "user type": "admin.userType",
  "account status": "admin.accountStatus",
  "last active": "admin.lastActive",
  "registration date": "admin.registrationDate",
  id: "admin.id",
  amount: "admin.amount",
  currency: "admin.currency",
  driver: "admin.driver",
  "created at": "admin.createdAt",
  "last updated": "admin.lastUpdated",
  "last update": "admin.lastUpdate",
  photo: "admin.photo",
  channel: "admin.channel",
  priority: "admin.priority",
  platform: "admin.platform",
  version: "admin.version",
  build: "admin.build",
  "force update": "admin.forceUpdate",
  "released at": "admin.releasedAt",
  image: "admin.image",
  "name (ar)": "admin.nameAr",
  "name (en)": "admin.nameEn",
  date: "admin.date",
  active: "admin.active",
  "required points": "admin.requiredPoints",
  "points/user": "admin.pointsPerUser",
  "executed at": "admin.executedAt",
  operation: "admin.operation",
  title: "admin.title",
  severity: "admin.severity",
  result: "admin.result",
  "action id": "admin.actionId",
  "actionid": "admin.actionId",
  "employee photo": "admin.employeePhoto",
  "employee name": "admin.employeeName",
  "employee id": "admin.employeeId",
  "employee role": "admin.employeeRole",
  "joined date": "admin.joinedDate",
  "last login": "admin.lastLogin",
  "wallet name": "admin.walletName",
  owner: "admin.owner",
  "current balance": "admin.currentBalance",
  "vehicle id": "admin.vehicleId",
  "vehicle picture": "admin.vehiclePicture",
  "vehicle name": "admin.vehicleName",
  model: "admin.model",
  color: "admin.color",
  "license plate": "admin.licensePlate",
  "trip id": "admin.tripId",
  "trip type": "admin.tripType",
  route: "admin.route",
  "start date / time": "admin.startDateTime",
  "end date / time": "admin.endDateTime",
  price: "admin.price",
  "payment id": "admin.paymentId",
  "driver name": "admin.driverName",
  "due date": "admin.dueDate",
  "payment date": "admin.paymentDate",
  "dispute id": "admin.disputeId",
  complainant: "admin.complainant",
  against: "admin.against",
  "assigned to": "admin.assignedTo",
  "request id": "admin.requestId",
  caller: "admin.caller",
  "date submitted": "admin.dateSubmitted",
  "employee report": "admin.employeeReport",
  "report id": "admin.reportId",
  "report name": "admin.reportName",
  "generated date": "admin.generatedDate",
  "report type": "admin.reportType",
  "time frame": "admin.timeFrame",
  "file type": "admin.fileType",
  "last export": "admin.lastExport",
  "role id": "admin.roleId",
  "role name": "admin.roleName",
  "number of employees": "admin.numberOfEmployees",
  "created date": "admin.createdDate",
  "record id": "admin.recordId",
  "contact name": "admin.contactName",
  "office reference": "admin.officeReference",
  "receipt image": "admin.receiptImage",
  "fuel type": "admin.fuelType",
  "seats number": "admin.seatsNumber",
  "current version": "admin.currentVersion",
  "new version": "admin.newVersion",
  "has pending edit": "admin.hasPendingEdit",
  insurance: "admin.insurance",
  "technical check": "admin.technicalCheck",
  inspection: "admin.inspection",
  "is active": "admin.isActive",
  code: "admin.code",
  "amount (syp)": "admin.amount",
};

// Update the AuthenticatedImage component with better handling for public images
const AuthenticatedImage = ({
  src,
  alt,
  className,
  fallback,
  onLoad,
  onError,
}) => {
  const [imageUrl, setImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      if (!src) {
        setIsLoading(false);
        setError(true);
        return;
      }

      // Check if the image is private (from your API domain)
      const isPrivateImage = src.includes("/api/images/private/");

      if (isPrivateImage) {
        try {
          const token = sessionStorage.getItem("ecoToken");

          // If no token, show fallback
          if (!token) {
            setError(true);
            setIsLoading(false);
            return;
          }

          const response = await fetch(src, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "image/*",
              // Add other headers if needed
            },
          });

          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageUrl(objectUrl);
            setError(false);
          } else if (response.status === 401) {
            // Token might be expired
            setError(true);
          } else {
            setError(true);
          }
        } catch (error) {
          console.error("Error loading authenticated image:", error);
          setError(true);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Public image - load normally
        try {
          // Create a test image to check if URL is valid
          const img = new Image();
          img.onload = () => {
            setImageUrl(src);
            setError(false);
            setIsLoading(false);
          };
          img.onerror = () => {
            setError(true);
            setIsLoading(false);
          };
          img.src = src;
        } catch (error) {
          console.error("Error loading public image:", error);
          setError(true);
          setIsLoading(false);
        }
      }
    };

    loadImage();

    // Cleanup function to revoke object URL
    return () => {
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [src]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={`animate-pulse bg-gray-200 ${className}`}>
        <div className="h-full w-full rounded-full"></div>
      </div>
    );
  }

  // Show error or fallback
  if (error || !imageUrl) {
    if (error && onError) {
      onError();
    }
    if (fallback) {
      return fallback;
    }

    return (
      <div
        className={`bg-gray-200 flex items-center justify-center ${className}`}
      >
        <span className="text-gray-500 text-xs">No image</span>
      </div>
    );
  }

  // Show the image
  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      onLoad={() => {
        if (onLoad) onLoad();
      }}
      onError={() => {
        setError(true);
        if (onError) onError();
      }}
    />
  );
};

// ProfilePicture component that uses AuthenticatedImage
const ProfilePictureCell = ({ imageUrl, fullName, className = "w-8 h-8" }) => {
  if (imageUrl) {
    return (
      <AuthenticatedImage
        src={imageUrl}
        alt={fullName || "Profile"}
        className={`rounded-full object-cover ${className}`}
        fallback={
          <div
            className={`bg-gray-300 rounded-full flex items-center justify-center ${className}`}
          >
            <span className="text-xs text-gray-600">
              {fullName?.charAt(0)?.toUpperCase() || "?"}
            </span>
          </div>
        }
      />
    );
  }

  return (
    <div
      className={`bg-gray-300 rounded-full flex items-center justify-center ${className}`}
    >
      <span className="text-xs text-gray-600">
        {fullName?.charAt(0)?.toUpperCase() || "?"}
      </span>
    </div>
  );
};

// VehiclePicture component for cars
const VehiclePictureCell = ({ imageUrl, alt, className = "w-12 h-12" }) => {
  if (imageUrl) {
    return (
      <AuthenticatedImage
        src={imageUrl}
        alt={alt || "Vehicle"}
        className={`rounded-lg object-cover ${className}`}
        fallback={
          <div
            className={`rounded-lg bg-gray-200 flex items-center justify-center ${className}`}
          >
            <span className="text-gray-400 text-xs">No Image</span>
          </div>
        }
      />
    );
  }

  return (
    <div
      className={`rounded-lg bg-gray-200 flex items-center justify-center ${className}`}
    >
      <span className="text-gray-400 text-xs">No Image</span>
    </div>
  );
};

export default function GeneralTable({
  tableHeader,
  tableData,
  error,
  isError,
  loading,
  type = "default",
  openModal,
  fun,
  loading2,
  onRetry,
  maxHeight,
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  const translateHeader = (header) => {
    if (header == null) return header;
    const key = TABLE_HEADER_I18N[String(header).toLowerCase().trim()];
    return key ? t(key) : header;
  };

  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewSrc, setImagePreviewSrc] = useState("");
  const [imagePreviewAlt, setImagePreviewAlt] = useState("");
  const [imagePreviewLoading, setImagePreviewLoading] = useState(false);

  const openImagePreview = (src, alt) => {
    if (!src) return;
    setImagePreviewSrc(src);
    setImagePreviewAlt(alt || "Image");
    setImagePreviewLoading(true);
    setImagePreviewOpen(true);
  };

  const closeImagePreview = () => {
    setImagePreviewOpen(false);
    setImagePreviewSrc("");
    setImagePreviewAlt("");
    setImagePreviewLoading(false);
  };

  const {
    handleMenuOpen,
    handleMenuClose,
    anchorEl,
    selectedRow,
    handleDelete,
    setChargeData,
    handleOpenModal,
    setType,
    setStatus,
    setBanType,
  } = useMyContext();

  // Function to render status with appropriate color
  const renderStatus = (status, statusColor = null) => {
    const statusColors = {
      // Support status colors - ADDED
      pending: "#F39C12", // Orange/Yellow
      Pending: "#F39C12",
      rejected: "#E74C3C", // Red
      Rejected: "#E74C3C",
      resolved: "#2ECC71", // Green
      Resolved: "#2ECC71",
      "in progress": "#3498DB", // Blue
      "In Progress": "#3498DB",
      "under review": "#F39C12", // Orange

      // Trip status colors
      Completed: "#2ECC71", // Green
      Ongoing: "#3498DB", // Blue
      onStarted: "#3498DB",
      Canceled: "#E74C3C", // Red
      Cancelled: "#E74C3C", // Red (alternative spelling)
      Pending: "#F39C12", // Gold/Orange
      ready: "#95A5A6",
      // Account status colors
      Active: "#2ECC71",
      Inactive: "#95A5A6",
      Supervised: "#F39C12",
      "Under Review": "#E74C3C",
      Done: "#2ECC71",
      Blocked: "#E74C3C",
      // Emergency numbers status
      true: "#2ECC71", // Active
      false: "#95A5A6", // Inactive

      // Payment status colors - ADDED
      Paid: "#2ECC71",
      Unpaid: "#F39C12",
      Overdue: "#E74C3C",

      // Admin Logs Severity Levels
      High: "#E74C3C", // Red
      Medium: "#F39C12", // Orange
      Low: "#3498DB", // Blue
      Critical: "#E74C3C", // Red
      Warning: "#F39C12", // Orange
      Info: "#3498DB", // Blue
      Success: "#2ECC71", // Green
      Failed: "#E74C3C", // Red

      // OCCASIONS STATUS COLORS - ADDED
      scheduled: "#F39C12", // Gold/Orange for scheduled (like pending)
      Scheduled: "#F39C12",
      executed: "#2ECC71", // Green for executed (like active)
      Executed: "#2ECC71",
      cancelled: "#E74C3C", // Red for cancelled

      // DISPUTES STATUS COLORS - ADDED
      pending: "#F39C12", // Orange/Yellow
      rejected: "#E74C3C", // Red
      Rejected: "#E74C3C",
      resolved: "#2ECC71", // Green
      Resolved: "#2ECC71",
      "in progress": "#3498DB", // Blue
      "In Progress": "#3498DB",
      "under review": "#F39C12", // Orange
      CurrentBalance: "#3498DB", // Blue

      // Color codes
      blue: "#3498DB",
      green: "#2ECC71",
      red: "#E74C3C",
      orange: "#F39C12",
      gold: "#F39C12",
      gray: "#95A5A6",
    };

    const color = statusColor || statusColors[status] || "#000";

    if (type === "support") {
      const displayStatus =
        status?.charAt(0)?.toUpperCase() + status?.slice(1) || "";
      return <span style={{ color, fontWeight: 600 }}>{displayStatus}</span>;
    }

    if (type === "numbers") {
      // For emergency numbers, show Active/Inactive based on boolean
      const statusText =
        status === true || status === "true" ? "Active" : "Inactive";
      return <span style={{ color, fontWeight: 600 }}>{statusText}</span>;
    }

    // For occasions type, show capitalized status
    if (type === "occasions") {
      const displayStatus =
        status?.charAt(0)?.toUpperCase() + status?.slice(1) || "";
      return <span style={{ color, fontWeight: 600 }}>{displayStatus}</span>;
    }

    return <span style={{ color, fontWeight: 600 }}>{status}</span>;
  };

  // Function to format date WITHOUT AM/PM
  const formatDate = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // Changed from true to false
      });
    } catch (error) {
      return dateString;
    }
  };

  // Function to format date as "T7-Oct-2025 10:02" (without AM/PM)
  const formatSupportDate = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      // Format like in the photo: "T7-Oct-2025 10:02"
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const dayOfWeek = days[date.getDay()];
      const dayNumber = date.getDate();

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `T${dayNumber}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  // Function to render actions for support type
  const renderSupportActions = (item) => {
    return (
      <Link
        to={`/support/details/${item?.id}`}
        className="w-24 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition cursor-pointer mx-auto"
      >{t("common.viewDetails")}</Link>
    );
  };

  // Function to render table cell content for support type
  const renderSupportTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase().replace(/\s+/g, "");

    switch (headerLower) {
      case "actionid":
      case "action id":
        return <span className="font-semibold text-gray-800">#{item.id}</span>;

      case "photo":
        return (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() =>
                openImagePreview(item.profile_image, item.user?.full_name)
              }
              disabled={!item.profile_image}
              className="disabled:cursor-default"
              style={{ lineHeight: 0 }}
            >
              <ProfilePictureCell
                imageUrl={item.profile_image}
                fullName={item.user?.full_name}
                className="w-10 h-10"
              />
            </button>
          </div>
        );

      case "name":
        return (
          <div className="flex flex-col items-center gap-3">
            <span className="font-medium text-gray-800">
              {item.user?.full_name}
            </span>
            <span
              className={`px-2.5 py-1 rounded-xl ${
                item?.user?.role === "passenger"
                  ? "bg-secondary/50 text-secondary"
                  : "bg-primary/50 text-primary"
              }`}
            >
              {item?.user?.role}
            </span>
          </div>
        );

      case "phone":
        return (
          <span className="text-gray-600">{item?.user?.phone_number}</span>
        );

      case "createdat":
      case "created at":
        return (
          <span className="text-gray-600">
            {formatSupportDate(item.created_at)}
          </span>
        );

      case "lastupdate":
      case "last update":
        return (
          <span className="text-gray-600">
            {formatSupportDate(item.updated_at)}
          </span>
        );

      case "type":
        return (
          <span className="font-medium text-gray-800">
            {item.category?.name_en || item.category?.name_ar || "Technical"}
          </span>
        );

      case "status":
        return (
          <div className="flex justify-center">{renderStatus(item.status)}</div>
        );

      case "channel":
        return <span className="text-gray-700">{item?.channel}</span>;

      case "actions":
        return renderSupportActions(item, rowIndex);

      default:
        return "-";
    }
  };

  // Function to render reports table cell
  const renderReportsTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "report id":
        return (
          <span className="font-semibold text-gray-800">
            {item.code || `AG${String(item.id).padStart(4, "0")}`}
          </span>
        );

      case "report name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "description":
        return <span className="text-gray-600">{item.description || "-"}</span>;

      case "generated date":
        return (
          <span className="text-gray-600">
            {item.generated_at ? formatDateForReports(item.generated_at) : "-"}
          </span>
        );

      case "report type":
        const reportType = item.report_type
          ? item.report_type.charAt(0).toUpperCase() + item.report_type.slice(1)
          : "-";
        return renderReportType(reportType);

      case "time frame":
        const timeFrame = item.time_frame
          ? item.time_frame.charAt(0).toUpperCase() + item.time_frame.slice(1)
          : "-";
        return <span className="text-gray-700">{timeFrame}</span>;

      case "file type":
        const fileType = item.file_type ? item.file_type.toUpperCase() : "-";
        return <span className="text-gray-700">{fileType}</span>;

      case "last export":
        const lastReportDate = item.last_exported_at
          ? formatDateForReports(item.last_exported_at)
          : "No previous export";
        return <span className="text-gray-600">{lastReportDate}</span>;

      case "export":
        // For download loading, we need to track which specific item is downloading
        // Since downloadFileLoading is global, we'll need to pass the downloading item ID from parent
        // For now, we'll use the global loading state
        const isDownloading = loading2; // This will show loading for ALL items when any is downloading

        return (
          <div className="flex justify-center items-center gap-2">
            {/* Re-Export Button */}
            <button
              onClick={() => {
                setType(item?.file_type);
                openModal("confirm", item?.id);
              }}
              disabled={isDownloading}
              className="w-20 h-8 rounded-[50px] disabled:bg-sixth/20 bg-sixth text-bg flex items-center justify-center text-sm font-medium hover:bg-blue-200 transition cursor-pointer"
            >
              Re-Export
            </button>

            {/* Three Dots Menu */}
            <div className="relative">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e, rowIndex);
                }}
                className="w-8 h-8"
                disabled={isDownloading}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && selectedRow === rowIndex}
                onClose={handleMenuClose}
                PaperProps={{
                  style: {
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    minWidth: "150px",
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    fun(item?.id);
                  }}
                  className="flex items-center gap-2 hover:bg-gray-50"
                  disabled={isDownloading}
                >
                  <span className="text-sm">
                    {isDownloading ? "Downloading..." : "Download"}
                  </span>
                </MenuItem>
              </Menu>
            </div>
          </div>
        );

      default:
        return "-";
    }
  };

  // Function to format date as "Oct 18, 2025" (date only, no time)
  const formatDateForReports = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Function to render table cell content for categories type
  const renderCategoriesTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return (
          <span className="font-semibold text-gray-800">
            #{String(item.id).padStart(5, "0")}
          </span>
        );

      case "name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "is active":
        return renderCategoryStatus(item.is_active);

      case "created at":
        return (
          <span className="text-gray-600">
            {item.created_at ? formatCategoryDate(item.created_at) : "-"}
          </span>
        );

      case "action":
        return renderCategoryActions(item, rowIndex);

      default:
        return "-";
    }
  };

  // Function to render status for categories (matching the photo style)
  const renderCategoryStatus = (isActive) => {
    const isActiveBool = isActive === true || isActive === "true";
    const color = isActiveBool ? "#2ECC71" : "#95A5A6";
    const text = isActiveBool ? "Active" : "Inactive";

    // Style from the photo - rounded badge with background color
    return (
      <span
        style={{
          color: color,
          fontWeight: 600,
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "14px",
          backgroundColor: `${color}15`, // 15% opacity of the color
        }}
      >
        {text}
      </span>
    );
  };

  // Function to format date for categories (matching the photo format) - WITHOUT AM/PM
  const formatCategoryDate = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      // Format like in the photo: "2024-01-15, 12:33"
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      // Format exactly like the photo: "2024-01-15, 12:33"
      return `${year}-${month}-${day}, ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  // Function to render actions for categories (matching the photo style)
  const renderCategoryActions = (item) => {
    return (
      <div className="flex items-center justify-center gap-2">
        {/* Edit Button - matching the photo style */}
        <button
          onClick={() => {
            setType("categories");
            openModal("addEditConfig", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center cursor-pointer hover:bg-secondary/80 transition"
          title={t("common.edit")}
        >
          <img src="/img/edit.svg" alt={t("common.edit")} className="w-3 h-3" />
        </button>

        {/* Delete Button - matching the photo style */}
        <button
          onClick={() => {
            setType("categories");
            openModal("delete", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-bg/70 flex items-center justify-center cursor-pointer hover:bg-bg/90 transition"
          title={t("common.delete")}
        >
          <img src="/img/delete.svg" alt={t("common.delete")} className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // ==============================
  // COLORS RENDER FUNCTIONS
  // ==============================

  // Function to render table cell content for colors type
  const renderColorsTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return (
          <span className="font-semibold text-gray-800">
            #{String(item.id).padStart(5, "0")}
          </span>
        );

      case "name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "hex code":
        return renderColorHexCode(item);

      case "is active":
        return renderStatusBadge(item.is_active);

      case "created at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.created_at)}
          </span>
        );

      case "updated at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.updated_at)}
          </span>
        );

      case "action":
        return renderStandardActions(item, "colors");

      default:
        return "-";
    }
  };

  // Function to render color hex code with color preview
  const renderColorHexCode = (item) => {
    const hexCode = item.hex_code || "#000000";

    return (
      <div className="flex items-center justify-center gap-2">
        <div
          className="w-8 h-8 rounded-full border border-gray-300"
          style={{ backgroundColor: hexCode }}
        />
        <span className="font-medium text-gray-800">{hexCode}</span>
      </div>
    );
  };

  // ==============================
  // FUEL TYPE RENDER FUNCTIONS
  // ==============================

  // Function to render table cell content for fuelType type
  const renderFuelTypeTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return (
          <span className="font-semibold text-gray-800">
            #{String(item.id).padStart(5, "0")}
          </span>
        );

      case "name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "is active":
        return renderStatusBadge(item.is_active);

      case "created at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.created_at)}
          </span>
        );

      case "updated at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.updated_at)}
          </span>
        );

      case "action":
        return renderStandardActions(item, "fuelType");

      default:
        return "-";
    }
  };

  // ==============================
  // INSURANCE RENDER FUNCTIONS
  // ==============================

  // Function to render table cell content for insurance type
  const renderInsuranceTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return (
          <span className="font-semibold text-gray-800">
            #{String(item.id).padStart(5, "0")}
          </span>
        );

      case "name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "is active":
        return renderStatusBadge(item.is_active);

      case "created at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.created_at)}
          </span>
        );

      case "updated at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.updated_at)}
          </span>
        );

      case "action":
        return renderStandardActions(item, "insurance");

      default:
        return "-";
    }
  };

  // ==============================
  // SERVICES RENDER FUNCTIONS
  // ==============================

  // Function to render table cell content for services type
  const renderServicesTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return (
          <span className="font-semibold text-gray-800">
            #{String(item.id).padStart(5, "0")}
          </span>
        );

      case "name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "icon":
        return renderServiceIcon(item);

      case "is active":
        return renderStatusBadge(item.is_active);

      case "created at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.created_at)}
          </span>
        );

      case "updated at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.updated_at)}
          </span>
        );

      case "action":
        return renderStandardActions(item, "services");

      default:
        return "-";
    }
  };

  // Function to render service icon with authentication
  const renderServiceIcon = (item) => {
    const iconUrl = item.icon_url || item.icon;

    if (iconUrl) {
      return (
        <div className="flex items-center justify-center">
          <AuthenticatedImage
            src={iconUrl}
            alt={item.name || "Service Icon"}
            className="w-8 h-8 object-contain"
            fallback={
              <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-xs">No Icon</span>
              </div>
            }
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center">
        <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
          <span className="text-gray-400 text-xs">No Icon</span>
        </div>
      </div>
    );
  };

  // ==============================
  // COMPANIES RENDER FUNCTIONS
  // ==============================

  // ==============================
  // MODELS RENDER FUNCTIONS
  // ==============================

  // Function to render table cell content for models type
  const renderModelsTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return (
          <span className="font-semibold text-gray-800">
            #{String(item.id).padStart(5, "0")}
          </span>
        );

      case "name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "is active":
        return renderStatusBadge(item.is_active);

      case "company":
        return (
          <span className="font-medium text-gray-800">
            {item.company?.name || "-"}
          </span>
        );

      case "created at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.created_at)}
          </span>
        );

      case "updated at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.updated_at)}
          </span>
        );

      case "action":
        return renderModelsActions(item);

      default:
        return "-";
    }
  };

  // Function to render actions for models type (with Edit and Delete buttons)
  const renderModelsActions = (item) => {
    return (
      <div className="flex items-center justify-center gap-2">
        {/* Edit Button - circular with edit icon */}
        <button
          onClick={() => {
            setType("models");
            openModal("addEditConfig", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center cursor-pointer hover:bg-secondary/80 transition"
          title={t("common.edit")}
        >
          <img src="/img/edit.svg" alt={t("common.edit")} className="w-3 h-3" />
        </button>

        {/* Delete Button - circular with delete icon */}
        <button
          onClick={() => {
            setType("models");
            openModal("delete", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-bg/70 flex items-center justify-center cursor-pointer hover:bg-bg/90 transition"
          title={t("common.delete")}
        >
          <img src="/img/delete.svg" alt={t("common.delete")} className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // ==============================
  // CURRENCIES RENDER FUNCTIONS
  // ==============================

  // Function to render table cell content for currencies type
  const renderCurrenciesTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return (
          <span className="font-semibold text-gray-800">
            #{String(item.id).padStart(5, "0")}
          </span>
        );

      case "code":
        return (
          <span className="font-medium text-gray-800">{item.code || "-"}</span>
        );

      case "name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "created at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.created_at)}
          </span>
        );

      case "updated at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.updated_at)}
          </span>
        );

      case "action":
        return renderCurrenciesActions(item);

      default:
        return "-";
    }
  };

  // Function to render actions for currencies type (with Edit and Delete buttons)
  const renderCurrenciesActions = (item) => {
    return (
      <div className="flex items-center justify-center gap-2">
        {/* Edit Button - circular with edit icon */}
        <button
          onClick={() => {
            setType("currencies");
            openModal("addEditConfig", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center cursor-pointer hover:bg-secondary/80 transition"
          title={t("common.edit")}
        >
          <img src="/img/edit.svg" alt={t("common.edit")} className="w-3 h-3" />
        </button>

        {/* Delete Button - circular with delete icon */}
        <button
          onClick={() => {
            setType("currencies");
            openModal("delete", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-bg/70 flex items-center justify-center cursor-pointer hover:bg-bg/90 transition"
          title={t("common.delete")}
        >
          <img src="/img/delete.svg" alt={t("common.delete")} className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // Function to render table cell content for companies type
  const renderCompaniesTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return (
          <span className="font-semibold text-gray-800">
            #{String(item.id).padStart(5, "0")}
          </span>
        );

      case "name":
        return (
          <span className="font-medium text-gray-800">{item.name || "-"}</span>
        );

      case "is active":
        return renderStatusBadge(item.is_active);

      case "models count":
      case "car models count":
        return (
          <span className="font-semibold text-blue-600">
            {item.car_models_count || 0}
          </span>
        );

      case "created at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.created_at)}
          </span>
        );

      case "updated at":
        return (
          <span className="text-gray-600">
            {formatStandardDate(item.updated_at)}
          </span>
        );

      case "action":
        return renderCompaniesActions(item);

      default:
        return "-";
    }
  };

  // Function to render actions for companies type (with Edit and Delete buttons)
  const renderCompaniesActions = (item) => {
    return (
      <div className="flex items-center justify-center gap-2">
        {/* Edit Button - circular with edit icon */}
        <button
          onClick={() => {
            setType("companies");
            openModal("addEditConfig", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center cursor-pointer hover:bg-secondary/80 transition"
          title={t("common.edit")}
        >
          <img src="/img/edit.svg" alt={t("common.edit")} className="w-3 h-3" />
        </button>

        {/* Delete Button - circular with delete icon */}
        <button
          onClick={() => {
            setType("companies");
            openModal("delete", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-bg/70 flex items-center justify-center cursor-pointer hover:bg-bg/90 transition"
          title={t("common.delete")}
        >
          <img src="/img/delete.svg" alt={t("common.delete")} className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // ==============================
  // SHARED/UTILITY FUNCTIONS
  // ==============================

  // Function to render status badge (shared by all types)
  const renderStatusBadge = (isActive) => {
    const isActiveBool = isActive === true || isActive === "true";
    const color = isActiveBool ? "#2ECC71" : "#95A5A6";
    const text = isActiveBool ? "Active" : "Inactive";

    return (
      <span
        style={{
          color: color,
          fontWeight: 600,
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "14px",
          backgroundColor: `${color}15`, // 15% opacity
        }}
      >
        {text}
      </span>
    );
  };

  // Function to format date in standard format (shared by all types) - WITHOUT AM/PM
  const formatStandardDate = (dateString) => {
    if (!dateString || dateString === "null" || dateString === "---")
      return "---";

    try {
      const date = new Date(dateString);

      // Format exactly like the photo: "2024-01-15, 12:33"
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day}, ${hours}:${minutes}`;
    } catch (error) {
      return "---";
    }
  };

  // Function to render standard actions (shared by all types)
  const renderStandardActions = (item, configType) => {
    return (
      <div className="flex items-center justify-center gap-2">
        {/* Edit Button */}
        <button
          onClick={() => {
            // Set the configuration type
            setType(configType);
            openModal("addEditConfig", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center cursor-pointer hover:bg-secondary/80 transition"
          title={t("common.edit")}
        >
          <img src="/img/edit.svg" alt={t("common.edit")} className="w-3 h-3" />
        </button>

        {/* Delete Button */}
        <button
          onClick={() => {
            setType(configType);
            openModal("delete", item?.id);
          }}
          className="w-7 h-7 rounded-full bg-bg/70 flex items-center justify-center cursor-pointer hover:bg-bg/90 transition"
          title={t("common.delete")}
        >
          <img src="/img/delete.svg" alt={t("common.delete")} className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // Function to render report type badge
  const renderReportType = (type) => {
    const typeMap = {
      Generalized: {
        text: "Generalized",
        color: "#3498DB",
        bgColor: "#3498DB15",
      },
      Financial: {
        text: "Financial",
        color: "#2ECC71",
        bgColor: "#2ECC7115",
      },
      Performance: {
        text: "Performance",
        color: "#F39C12",
        bgColor: "#F39C1215",
      },
    };

    const typeConfig = typeMap[type] || {
      text: type || "-",
      color: "#95A5A6",
      bgColor: "#95A5A615",
    };

    return (
      <span
        style={{
          color: typeConfig.color,
          fontWeight: 600,
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "14px",
          backgroundColor: typeConfig.bgColor,
        }}
      >
        {typeConfig.text}
      </span>
    );
  };

  // Function to format date for roles table (like the photo: "2025-09-30 15:35") - WITHOUT AM/PM
  const formatDateForRoles = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      // Format date as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Format time as HH:MM (24-hour format)
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${formattedDate} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  // Function to format date for driver payments (YYYY-MM-DD format)
  const formatDateForDriverPayments = (dateString) => {
    if (!dateString || dateString === "---") return "---";

    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      return dateString;
    }
  };

  // Function to format date for occasions (like "22 Sep 2025, 21:00") - WITHOUT AM/PM
  const formatDateForOccasions = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      // Format date as "22 Sep 2025"
      const day = date.getDate();
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const year = date.getFullYear();

      // Format time as "21:00" (24-hour format)
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  // Function to format executed_at date for occasions - WITHOUT AM/PM
  const formatExecutedDate = (dateString) => {
    if (!dateString) return "Not executed yet";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // Changed from true to false
      });
    } catch (error) {
      return dateString;
    }
  };

  // Function to render role ID (like in the photo: #81490, #81491, etc.)
  const renderRoleId = (id) => {
    if (!id) return "-";
    return `#${id}`;
  };

  // Function to format date for disputes (like "T7-Oct-2025 10:02") - WITHOUT AM/PM
  const formatDateForDisputes = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      // Format like in the photo: "T7-Oct-2025 10:02"
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const dayOfWeek = days[date.getDay()];
      const dayNumber = date.getDate();

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `T${dayNumber}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      // Fallback to simpler format
      return new Date(dateString).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // Changed from true to false
      });
    }
  };

  // Function to calculate deadline (assuming 3 days from creation)
  const calculateDeadline = (createdAt) => {
    if (!createdAt) return "-";

    try {
      const createdDate = new Date(createdAt);
      const deadlineDate = new Date(createdDate);
      deadlineDate.setDate(createdDate.getDate() + 3); // 3 days deadline

      // Calculate time remaining
      const now = new Date();
      const diffMs = deadlineDate - now;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );

      if (diffMs <= 0) {
        return "Expired";
      }

      return `${diffDays} days ${diffHours} hours`;
    } catch (error) {
      return "-";
    }
  };

  // Function to render table cell content for rewardsTransactions type
  const renderRewardsTransactionsTableCell = (header, item) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "user":
        return renderUserInfo(item);

      case "type":
        return renderRewardsTransactionType(item.type);

      case "source type":
        return renderSourceType(item.source_type);

      case "source id":
        return renderSourceId(item.source_type, item.source_id);

      case "points delta":
        return renderPointsDelta(item.delta_points);

      case "balance after":
        return (
          <span className="font-semibold text-gray-800">
            {item.balance_after || "0"}
          </span>
        );

      case "description":
        return <span className="text-gray-600">{item.description || "-"}</span>;

      case "date-time":
        return formatDateForRewardsTransactions(item.created_at);

      default:
        return "-";
    }
  };

  const renderIncidencStatus = (status) => {
    const statusMap = {
      under_review: "Under Review",
      in_progress: "In Progress",
      resolved: "Resolved",
      // Add other status mappings as needed
    };

    const displayStatus =
      statusMap[status] ||
      status?.replace(/_/g, " ")?.replace(/\b\w/g, (l) => l.toUpperCase()) ||
      status;

    return renderStatus(displayStatus);
  };

  // Function to format type for display
  const formatIncidencType = (type) => {
    const typeMap = {
      medical_emergency: "Medical Emergency",
      vehicle_breakdown: "Vehicle Breakdown",
      // Add other type mappings as needed
    };

    return (
      typeMap[type] ||
      type?.replace(/_/g, " ")?.replace(/\b\w/g, (l) => l.toUpperCase()) ||
      type
    );
  };

  // Function to render employee report cell
  const renderEmployeeReport = (hasReport, id) => {
    if (hasReport) {
      return (
        <div className="flex items-center jap-3">
          <button
            onClick={() => openModal("employeeReport", id)}
            className="w-16 h-7 rounded-[50px] bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium hover:bg-blue-200 transition cursor-pointer mx-auto"
          >
            show
          </button>
          <button
            onClick={() => openModal("saveReport", id)}
            className="w-25 h-7  rounded-[50px] bg-primary/30 text-primary flex items-center justify-center text-sm font-medium  transition cursor-pointer mx-auto"
          >
            Update Report
          </button>
        </div>
      );
    } else {
      return (
        <button
          onClick={() => openModal("saveReport", id)}
          className="w-25 h-7  rounded-[50px] bg-primary/30 text-primary flex items-center justify-center text-sm font-medium  transition cursor-pointer mx-auto"
        >
          Add Report
        </button>
      );
    }
  };

  // Function to render actions for incidenc type
  const renderIncidencActions = (item, rowIndex) => {
    return (
      <div className="flex items-center justify-center gap-2">
        {/* Details button */}
        <button
          onClick={() => openModal("emergency", item?.id)}
          className="w-20 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition cursor-pointer"
        >
          details
        </button>

        {/* Three Dots Menu */}
        {(item?.status === "under_review" ||
          item?.status === "in_progress" ||
          (item?.status !== "resolved" && item?.status !== "rejected")) && (
          <div className="relative">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, rowIndex);
              }}
              className="w-8 h-8"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && selectedRow === rowIndex}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  minWidth: "180px",
                },
              }}
            >
              {/* Change Status Menu Item - فقط لـ under_review */}
              {item?.status === "under_review" && (
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    openModal("confirm", item?.id);
                  }}
                  className="flex items-center gap-2 hover:bg-gray-50"
                >
                  <span className="text-sm">{t("admin.changeStatus")}</span>
                </MenuItem>
              )}

              {/* Resolve/Reject Menu Items - فقط لـ in_progress */}
              {item?.status === "in_progress" && (
                <>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      openModal("confirm", item?.id);
                      setStatus("resolved");
                    }}
                    className="flex items-center gap-2 hover:bg-gray-50"
                  >
                    <span className="text-sm">{t("admin.resolve")}</span>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      openModal("confirm", item?.id);
                      setStatus("rejected");
                    }}
                    className="flex items-center gap-2 hover:bg-gray-50"
                  >
                    <span className="text-sm">{t("admin.reject")}</span>
                  </MenuItem>
                </>
              )}
            </Menu>
          </div>
        )}
      </div>
    );
  };

  const renderIncidencTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "request id":
        return (
          <span className="font-semibold text-gray-800">
            {item.request_code || `#${item.id}`}
          </span>
        );

      case "caller":
        return (
          <div className="text-center flex flex-col gap-3">
            <div className="font-medium text-gray-800">
              {item.caller_name || "-"}
            </div>
            <div
              className={`text-xs py-1 px-3 rounded-2xl ${
                item.caller_role === "passenger"
                  ? "bg-secondary/50 text-secondary"
                  : "bg-primary/50 text-primary"
              }`}
            >
              {item.caller_role || ""}
            </div>
          </div>
        );

      case "driver":
        return (
          <span className="font-medium text-gray-800 text-center block">
            {item.driver_name || "—"}
          </span>
        );

      case "type":
        return (
          <span className="font-medium text-gray-800 text-center block">
            {formatIncidencType(item.type)}
          </span>
        );

      case "trip id":
        return (
          <span className="font-semibold text-blue-600 text-center block">
            {item.trip_id ? `#${item.trip_id}` : "—"}
          </span>
        );

      case "date submitted":
        return (
          <span className="text-gray-600">
            {item.date_submitted || "-"}
          </span>
        );

      case "status":
        return (
          <div className="flex justify-center">{renderIncidencStatus(item.status)}</div>
        );

      case "employee report":
        return (
          <div className="flex justify-center">
            {renderEmployeeReport(item.has_employee_report, item.id)}
          </div>
        );

      case "actions":
        return renderIncidencActions(item, rowIndex);

      default:
        return "-";
    }
  };

  // Function to render user information (Name and Phone)
  const renderUserInfo = (item) => {
    // In your actual implementation, you might need to fetch user details
    // For now, using the data you have
    return (
      <div className="text-left">
        <div className="font-medium text-gray-800">
          {item?.user_full_name || `User #${item.user_id}`}
        </div>
      </div>
    );
  };

  // Function to render transaction type with appropriate styling
  const renderRewardsTransactionType = (type) => {
    const typeMap = {
      earn: {
        text: "EARN",
        color: "#2ECC71", // Green for earn
        bgColor: "#2ECC7115", // 15% opacity
      },
      spend: {
        text: "SPEND",
        color: "#E74C3C", // Red for spend
        bgColor: "#E74C3C15", // 15% opacity
      },
      redeem: {
        text: "REDEEM",
        color: "#F39C12", // Orange for redeem
        bgColor: "#F39C1215", // 15% opacity
      },
    };

    const typeConfig = typeMap[type?.toLowerCase()] || {
      text: type?.toUpperCase() || "-",
      color: "#95A5A6",
      bgColor: "#95A5A615",
    };

    return (
      <span
        style={{
          color: typeConfig.color,
          fontWeight: 600,
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "14px",
          backgroundColor: typeConfig.bgColor,
        }}
      >
        {typeConfig.text}
      </span>
    );
  };

  // Function to render source type
  const renderSourceType = (sourceType) => {
    const typeMap = {
      trip: "TRIP",
      occasion: "OCCASION",
      reward: "REWARD",
      admin: "ADMIN",
      manual: "MANUAL",
      referral: "REFERRAL",
    };

    const displayType =
      typeMap[sourceType?.toLowerCase()] || sourceType?.toUpperCase() || "-";

    return <span className="font-medium text-gray-800">{displayType}</span>;
  };

  // Function to render source ID with prefix
  const renderSourceId = (sourceType, sourceId) => {
    if (!sourceId) return "-";

    const prefixMap = {
      trip: "TRP-",
      occasion: "OCC-",
      reward: "REW-",
      admin: "ADM-",
    };

    const prefix = prefixMap[sourceType?.toLowerCase()] || "";
    return `${prefix}${sourceId}`;
  };

  // Function to render points delta with +/- sign and color
  const renderPointsDelta = (deltaPoints) => {
    if (deltaPoints === undefined || deltaPoints === null) return "0";

    const isPositive = deltaPoints > 0;
    const sign = isPositive ? "+" : "";
    const color = isPositive ? "#2ECC71" : "#E74C3C";

    return (
      <span style={{ color, fontWeight: 600 }}>
        {sign}
        {deltaPoints}
      </span>
    );
  };

  // Function to format date for rewards transactions (like in the photo) - WITHOUT AM/PM
  const formatDateForRewardsTransactions = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      // Format exactly like the photo: "2025-01-15 10:15"
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  // Function to render actions for occasions type
  const renderAppVersionsActions = (item) => {
    const isActive = Boolean(item.is_active);

    return (
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <button
          onClick={() => openModal("appVersionDetails", item?.id)}
          className="w-24 h-8 rounded-[50px] bg-gray-100 text-gray-800 flex items-center justify-center text-sm font-medium hover:bg-gray-200 transition cursor-pointer"
          title={t("common.viewDetails")}
        >
          {t("admin.view")}
        </button>

        <button
          onClick={() => openModal("addEditAppVersion", item?.id)}
          className="w-20 h-8 rounded-[50px] bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium hover:bg-blue-200 transition cursor-pointer"
          title={t("common.edit")}
        >
          {t("common.edit")}
        </button>

        <button
          onClick={() =>
            openModal(
              "confirm",
              item?.id,
              isActive ? "deactivate" : "activate",
            )
          }
          className={`w-28 h-8 rounded-[50px] flex items-center justify-center text-sm font-medium transition cursor-pointer ${
            isActive
              ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
              : "bg-green-100 text-green-700 hover:bg-green-200"
          }`}
          title={isActive ? t("admin.deactivate") : t("admin.activate")}
        >
          {isActive ? t("admin.deactivate") : t("admin.activate")}
        </button>

        <button
          onClick={() => handleDelete("delete", item?.id)}
          className="w-20 h-8 rounded-[50px] bg-red-100 text-red-700 flex items-center justify-center text-sm font-medium hover:bg-red-200 transition cursor-pointer"
          title={t("common.delete")}
        >
          {t("common.delete")}
        </button>
      </div>
    );
  };

  const renderAppVersionsTableCell = (header, item) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return <span className="font-semibold">#{item.id}</span>;

      case "platform":
        return (
          <span className="uppercase font-medium text-gray-800">
            {item.platform || "-"}
          </span>
        );

      case "version":
        return (
          <span className="font-semibold text-primary">{item.version || "-"}</span>
        );

      case "build":
        return <span>{item.build_number || "-"}</span>;

      case "force update":
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${
              item.force_update
                ? "bg-red-100 text-red-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {item.force_update ? "Yes" : "No"}
          </span>
        );

      case "status":
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${
              item.is_active
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            {item.is_active ? "Active" : "Inactive"}
          </span>
        );

      case "released at":
        return (
          <span className="text-gray-600 text-sm">
            {formatDate(item.released_at)}
          </span>
        );

      case "actions":
        return renderAppVersionsActions(item);

      default:
        return "-";
    }
  };

  const renderOccasionsActions = (item, rowIndex) => {
    const isScheduled = item.status === "scheduled";
    const isExecuted = item.status === "executed";

    return (
      <div className="flex items-center gap-2">
        {/* Edit Button - Only for scheduled occasions */}

        <button
          onClick={() => openModal("addEditOccation", item?.id)}
          className="w-20 h-8 rounded-[50px] bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium hover:bg-blue-200 transition cursor-pointer"
          title={t("common.edit")}
        >
          {t("common.edit")}
        </button>

        {/* Execute Button - Only for scheduled occasions */}
        {isScheduled && (
          <button
            onClick={() => openModal("confirm", item?.id)}
            className="w-24 h-8 rounded-[50px] bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium hover:bg-green-200 transition cursor-pointer"
            title={t("admin.execute")}
          >
            {t("admin.execute")}
          </button>
        )}

        {/* Delete Button - For all statuses */}
        <button
          onClick={() => handleDelete("delete", item?.id)}
          className="w-20 h-8 rounded-[50px] bg-red-100 text-red-700 flex items-center justify-center text-sm font-medium hover:bg-red-200 transition cursor-pointer"
          title={t("common.delete")}
        >
          {t("common.delete")}
        </button>
      </div>
    );
  };

  // Function to render table cell content for occasions type
  const renderOccasionsTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "name (ar)":
        return (
          <span className="text-right font-medium text-gray-800">
            {item.name_ar || "-"}
          </span>
        );

      case "name (en)":
        return (
          <span className="font-medium text-gray-800">
            {item.name_en || "-"}
          </span>
        );

      case "date":
        return (
          <span className="text-gray-600">
            {formatDateForOccasions(item.date)}
          </span>
        );

      case "points/user":
        return (
          <span className="font-semibold text-blue-600">
            {item.points_per_user || 0}
          </span>
        );

      case "status":
        return renderStatus(item.status);

      case "executed at":
        return (
          <span className="text-gray-600">
            {formatExecutedDate(item.executed_at) || "-"}
          </span>
        );

      case "actions":
        return renderOccasionsActions(item, rowIndex);

      default:
        return "-";
    }
  };

  // Function to render table cell content for rewards type (NEW)
  const renderRewardsTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "image":
        return renderRewardsImage(item);

      case "name (ar)":
        return (
          <span className="text-right font-medium text-gray-800">
            {item.name_ar || "-"}
          </span>
        );

      case "name (en)":
        return (
          <span className="font-medium text-gray-800">
            {item.name_en || "-"}
          </span>
        );

      case "type":
        return renderRewardsType(item.type);

      case "required points":
        return (
          <span className="font-semibold text-blue-600">
            {item.required_points || 0}
          </span>
        );

      case "active":
        return renderRewardsActive(item.active);

      case "actions":
        return renderRewardsActions(item);

      default:
        return "-";
    }
  };

  // Function to render image for rewards with authentication
  const renderRewardsImage = (item) => {
    const imageUrl = item.image_path || item.image;

    if (imageUrl) {
      return (
        <div className="flex items-center justify-center">
          <AuthenticatedImage
            src={imageUrl}
            alt={item.name_en || item.name_ar || "Reward"}
            className="w-20 h-20 rounded-lg object-cover"
            fallback={
              <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-xs">No Image</span>
              </div>
            }
          />
        </div>
      );
    }

    if (item.images && item.images.length > 0) {
      return (
        <div className="flex items-center justify-center">
          <AuthenticatedImage
            src={item.images[0]}
            alt={item.name_en || item.name_ar || "Reward"}
            className="w-12 h-12 rounded-lg object-cover"
            fallback={
              <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-xs">No Image</span>
              </div>
            }
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center">
        <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
          <span className="text-gray-400 text-xs">No Image</span>
        </div>
      </div>
    );
  };

  // Function to render rewards type
  const renderRewardsType = (type) => {
    const typeMap = {
      external_product: "External Product",
      trip_discount_percent: "Trip Discount Percent",
      trip_discount_amount: "Trip Discount Amount",
      product: "Product",
      discount: "Discount",
      voucher: "Voucher",
      cashback: "Cashback",
      gift_card: "Gift Card",
    };

    const displayType =
      typeMap[type] ||
      type
        ?.replace(/_/g, " ")
        ?.split(" ")
        ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        ?.join(" ") ||
      type ||
      "-";

    return <span className="font-medium text-gray-800">{displayType}</span>;
  };

  // Function to render active status for rewards
  const renderRewardsActive = (active) => {
    const isActive = active === true || active === "true";
    const color = isActive ? "#2ECC71" : "#95A5A6";
    const text = isActive ? "Active" : "Inactive";

    return (
      <span
        style={{
          color,
          fontWeight: 600,
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "14px",
          backgroundColor: `${color}15`,
        }}
      >
        {text}
      </span>
    );
  };

  // Function to render actions for rewards type
  const renderRewardsActions = (item) => {
    return (
      <div className="flex items-center gap-2">
        {/* Edit Button */}
        <button
          onClick={() => openModal("addEditReword", item?.id)}
          className="w-20 h-8 rounded-[50px] bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium hover:bg-blue-200 transition cursor-pointer"
          title={t("common.edit")}
        >
          {t("common.edit")}
        </button>

        {/* Delete Button */}
        <button
          onClick={() => handleDelete("delete", item?.id)}
          className="w-20 h-8 rounded-[50px] bg-red-100 text-red-700 flex items-center justify-center text-sm font-medium hover:bg-red-200 transition cursor-pointer"
          title={t("common.delete")}
        >
          {t("common.delete")}
        </button>
      </div>
    );
  };

  // NEW: Helper functions for logs type (matching the new photo)
  const renderLogsActionId = (id) => {
    return `#${id}`;
  };

  const renderLogsEmployeePhoto = (admin) => {
    // If admin has a photo URL, use it with authentication
    if (admin?.photo_url) {
      return (
        <AuthenticatedImage
          src={admin.photo_url}
          alt={admin.name || "Admin"}
          className="w-10 h-10 rounded-full object-cover mx-auto"
          fallback={
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mx-auto">
              <span className="text-sm text-gray-600 font-medium">
                {admin.name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase() || "A"}
              </span>
            </div>
          }
        />
      );
    }

    // If no photo but has name, show initial
    if (admin?.name) {
      return (
        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mx-auto">
          <span className="text-sm text-gray-600 font-medium">
            {admin.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()}
          </span>
        </div>
      );
    }

    // Default case
    return (
      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
        <span className="text-sm text-blue-600 font-medium">A</span>
      </div>
    );
  };

  const renderLogsEmployeeName = (admin) => {
    return admin?.name || "Unknown";
  };

  const renderLogsRole = (admin) => {
    if (!admin?.role?.name) return "-";

    const roleName = admin.role.name;
    // Capitalize first letter of each word
    return roleName
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderLogsDateTime = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      // Format like in the new photo: "T7-Oct-2025 10:02"
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const dayOfWeek = days[date.getDay()];
      const dayNumber = date.getDate();

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `T${dayNumber}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  const renderLogsOperation = (action) => {
    // Map operations to their display text and colors
    const operationMap = {
      delete: {
        text: "Delete",
        color: "#E74C3C", // Red
      },
      remove: {
        text: "Delete",
        color: "#E74C3C", // Red
      },
      login: {
        text: "Login",
        color: "#3498DB", // Blue
      },
      logout: {
        text: "Logout",
        color: "#3498DB", // Blue
      },
      approve: {
        text: "Approve",
        color: "#F39C12", // Orange
      },
      reject: {
        text: "Reject",
        color: "#F39C12", // Orange
      },
      generate: {
        text: "Generate Report",
        color: "#2ECC71", // Green
      },
      report: {
        text: "Generate Report",
        color: "#2ECC71", // Green
      },
      update: {
        text: "Update",
        color: "#3498DB", // Blue
      },
      edit: {
        text: "Update",
        color: "#3498DB", // Blue
      },
      create: {
        text: "Add",
        color: "#3498DB", // Blue (assuming Add is blue like Update)
      },
      add: {
        text: "Add",
        color: "#3498DB", // Blue
      },
    };

    const lowerAction = action?.toLowerCase() || "";

    // Check for exact matches first
    for (const [key, operation] of Object.entries(operationMap)) {
      if (lowerAction === key || lowerAction.includes(key)) {
        return (
          <span
            style={{
              color: operation.color,
              fontWeight: 600,
              display: "inline-block",
            }}
          >
            {operation.text}
          </span>
        );
      }
    }

    // Special handling for "Generate Report" which is two words
    if (lowerAction.includes("generate") || lowerAction.includes("report")) {
      return (
        <span
          style={{
            color: "#2ECC71", // Green
            fontWeight: 600,
            display: "inline-block",
          }}
        >
          Generate Report
        </span>
      );
    }

    // Special handling for "Approve" variations
    if (lowerAction.includes("approve")) {
      return (
        <span
          style={{
            color: "#F39C12", // Orange
            fontWeight: 600,
            display: "inline-block",
          }}
        >
          Approve
        </span>
      );
    }

    // Default: capitalize first letter with black color
    return (
      <span
        style={{ color: "#000000", fontWeight: 600, display: "inline-block" }}
      >
        {action?.charAt(0)?.toUpperCase() + action?.slice(1) || "-"}
      </span>
    );
  };

  const renderLogsTitle = (entity, action) => {
    // Capitalize the entity name first
    const capitalizedEntity =
      entity?.charAt(0)?.toUpperCase() + entity?.slice(1) || "";

    // Map entities and actions to titles like in the new photo
    const titleMap = {
      Trip: {
        delete: "Remove Trip",
        remove: "Remove Trip",
        create: "New Trip",
        add: "New Trip",
        update: "Update Trip",
        edit: "Update Trip",
      },
      Role: {
        create: "New Role",
        add: "New Role",
        update: "Update Role",
        edit: "Update Role",
        delete: "Remove Role",
      },
      User: {
        create: "New User",
        add: "New User",
        update: "Update User",
        edit: "Update User",
        delete: "Remove User",
        login: "Admin Login",
        logout: "Admin Logout",
      },
      Complaint: {
        approve: "Approve Complaint",
        reject: "Reject Complaint",
      },
      Refund: {
        reject: "Reject Refund Request",
      },
      Employee: {
        create: "New Employee",
        add: "New Employee",
        update: "Update Employee",
        delete: "Remove Employee",
      },
    };

    // Try exact match first
    if (titleMap[capitalizedEntity] && titleMap[capitalizedEntity][action]) {
      return titleMap[capitalizedEntity][action];
    }

    // Fallback: Create title from entity and action
    const operation = renderLogsOperation(action);
    // Return the operation as string, not as JSX element
    const operationText = getActionText(action);
    return `${operationText} ${capitalizedEntity}`;
  };

  const renderLogsDescription = (entity, action, item) => {
    const entityId = item.entity_id;
    const adminName = item.admin?.name || "Admin";

    // Map to descriptions like in the new photo
    const descriptionMap = {
      Trip: {
        delete: `Deleted trip BX06 scheduled`,
        remove: `Deleted trip BX06 scheduled`,
      },
      Role: {
        create: `Added a new role to the system`,
        add: `Added a new role to the system`,
        update: `Updated role information`,
        delete: `Removed a role from the system`,
      },
      User: {
        login: `Logged in to the admin dashboard`,
        logout: `Logged out from dashboard`,
      },
      Complaint: {
        approve: `Approved complaint #Z10`,
        reject: `Rejected complaint #Z10`,
      },
      Refund: {
        reject: `Rejected refund request #180`,
      },
      Employee: {
        create: `Added a new employee to the system`,
        add: `Added a new employee to the system`,
        update: `Updated employee information`,
        delete: `Removed an employee from the system`,
      },
    };

    // Try exact match first
    if (descriptionMap[entity] && descriptionMap[entity][action]) {
      return descriptionMap[entity][action];
    }

    // Fallback description based on action
    // Get the action text without the JSX wrapper
    const actionText = getActionText(action);
    const lowerAction = actionText.toLowerCase();

    if (entityId) {
      return `${adminName} ${lowerAction}ed ${entity.toLowerCase()} #${entityId}`;
    }

    return `${adminName} ${lowerAction}ed a ${entity.toLowerCase()}`;
  };

  // Helper function to get action text as string
  const getActionText = (action) => {
    const actionMap = {
      delete: "Delete",
      remove: "Delete",
      login: "Login",
      logout: "Logout",
      approve: "Approve",
      reject: "Reject",
      generate: "Generate Report",
      report: "Generate Report",
      update: "Update",
      edit: "Update",
      create: "Add",
      add: "Add",
    };

    const lowerAction = action?.toLowerCase() || "";

    // Check for exact matches
    for (const [key, text] of Object.entries(actionMap)) {
      if (lowerAction === key || lowerAction.includes(key)) {
        return text;
      }
    }

    // Special handling for "Generate Report"
    if (lowerAction.includes("generate") || lowerAction.includes("report")) {
      return "Generate Report";
    }

    // Special handling for "Approve" variations
    if (lowerAction.includes("approve")) {
      return "Approve";
    }

    // Default: capitalize first letter
    return action?.charAt(0)?.toUpperCase() + action?.slice(1) || "";
  };

  const renderLogsSeverity = (action, entity) => {
    const severityMap = {
      delete: {
        Trip: "High",
        Role: "Medium",
        Employee: "Medium",
        default: "Medium",
      },
      remove: {
        Trip: "High",
        default: "Medium",
      },
      login: {
        default: "Low",
      },
      logout: {
        default: "Low",
      },
      approve: {
        default: "Medium",
      },
      reject: {
        default: "Medium",
      },
      create: {
        Role: "Low",
        Employee: "Low",
        default: "Low",
      },
      add: {
        default: "Low",
      },
      update: {
        default: "Low",
      },
      edit: {
        default: "Low",
      },
    };

    const lowerAction = action?.toLowerCase() || "";

    // Find matching action
    for (const [actionKey, entities] of Object.entries(severityMap)) {
      if (lowerAction.includes(actionKey)) {
        if (entities[entity]) {
          return entities[entity];
        }
        if (entities.default) {
          return entities.default;
        }
      }
    }

    return "Low"; // Default
  };

  const renderLogsResult = (success) => {
    const result = success ? "Success" : "Fail";
    const color = success ? "#2ECC71" : "#E74C3C";
    return <span style={{ color, fontWeight: 600 }}>{result}</span>;
  };

  // NEW: Function to render logs table cell
  const renderLogsTableCell = (header, item) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "action id":
        return renderLogsActionId(item.id);

      case "employee photo":
        return renderLogsEmployeePhoto(item.admin);

      case "employee name":
        return renderLogsEmployeeName(item.admin);

      case "role":
        return renderLogsRole(item.admin);

      case "date & time":
        return renderLogsDateTime(item.created_at);

      case "operation":
        return renderLogsOperation(item.action);

      case "title":
        return renderLogsTitle(item.entity, item.action);

      case "description":
        return renderLogsDescription(item.entity, item.action, item);

      case "severity":
        const severity = renderLogsSeverity(item.action, item.entity);
        return renderStatus(severity);

      case "result":
        return renderLogsResult(item.success);

      case "actions":
        return (
          <button
            className="w-24 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition cursor-pointer mx-auto"
            onClick={() => openModal("actionDetails", item.id)}
          >{t("common.viewDetails")}</button>
        );

      default:
        return "-";
    }
  };

  // Function to render table cell content for transaction type
  const renderTransactionTableCell = (header, item) => {
    const headerLower = header.toLowerCase();
    if (headerLower === "transaction id") {
      return `#${item?.transaction_id}`;
    }

    switch (headerLower) {
      case "type":
        return item?.type || "-";
      case "amount":
        const amount = item?.amount || "0";
        const isPositive = amount.startsWith("+");
        const isNegative = amount.startsWith("-");
        const color = isPositive
          ? "#2ECC71"
          : isNegative
            ? "#E74C3C"
            : "#000000";

        return <span style={{ color, fontWeight: 600 }}>{amount}</span>;
      case "date":
        return item.date ? formatDateForRoles(item.date) : "-";
      case "status":
        return renderStatus(item?.status);
      case "bill":
        // Check if bill_images exists and has a valid URL
        if (item?.bill_images?.[0]?.url) {
          return (
            <button
              type="button"
              onClick={() => openImagePreview(item.bill_images[0].url, "Bill")}
              className="cursor-zoom-in"
              title="Click to enlarge"
              style={{ width: "100px", height: "60px", overflow: "hidden" }}
            >
              <AuthenticatedImage
                src={item.bill_images[0].url}
                alt="Bill"
                className="w-full h-full object-cover rounded"
                fallback={
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded">
                    <span className="text-gray-500 text-xs">No Image</span>
                  </div>
                }
              />
            </button>
          );
        } else if (item?.bill_images && typeof item.bill_images === "string") {
          // If bill_images is a string URL directly
          return (
            <button
              type="button"
              onClick={() => openImagePreview(item.bill_images, "Bill")}
              className="cursor-zoom-in"
              title="Click to enlarge"
              style={{ width: "100px", height: "60px", overflow: "hidden" }}
            >
              <AuthenticatedImage
                src={item.bill_images}
                alt="Bill"
                className="w-full h-full object-cover rounded"
                fallback={
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded">
                    <span className="text-gray-500 text-xs">No Image</span>
                  </div>
                }
              />
            </button>
          );
        }
        return "-";
    }
  };

  const setDataToCharge = (id, balance, name) => {
    setChargeData({
      name: name,
      balance: balance,
      target_user_id: id,
      currency_id: 1,
    });
    handleOpenModal("charge");
  };

  // Function to render table cell content for wallets action
  const renderWalletsActions = (item) => {
    return (
      <div className="flex items-center gap-4">
        <button
          onClick={() => openModal("transaction", item?.wallet_id)}
          className="w-[80px] h-[33px] rounded-[50px] bg-primary text-white cursor-pointer"
        >
          logs
        </button>
        <button
          onClick={() =>
            setDataToCharge(item?.user_id, item?.current_balance, item?.owner)
          }
          className="w-[80px] h-[33px] rounded-[50px] bg-secondary text-white cursor-pointer"
        >
          charge
        </button>
      </div>
    );
  };

  // Function to render table cell content for wallets type
  const renderWalletsTableCell = (header, item) => {
    const headerLower = header.toLowerCase();
    if (headerLower === "wallet name") {
      return `#${item?.wallet_name}`;
    }
    switch (headerLower) {
      case "owner":
        return item?.owner;
      case "current balance":
        return <span className="text-[#2ECC71]">{item?.current_balance}</span>;
      case "last updated":
        return item?.last_updated;
      case "actions":
        return renderWalletsActions(item);
    }
  };

  // Function to render table cell content for employees type
  const renderEmployeesTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    if (headerLower === "employee id" || headerLower === "employee") {
      return `#${item.id}`;
    }

    switch (headerLower) {
      case "employee photo":
        return (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() => openImagePreview(item.photo_url, item.name)}
              disabled={!item.photo_url}
              className="disabled:cursor-default"
              style={{ lineHeight: 0 }}
            >
              <ProfilePictureCell
                imageUrl={item.photo_url}
                fullName={item.name}
                className="w-10 h-10"
              />
            </button>
          </div>
        );

      case "full name":
        return item.name || "-";

      case "phone number":
        return item.phone_number || "-";

      case "employee role":
        return item.role?.name || "-";

      case "joined date":
        return item.created_at ? formatDateForRoles(item.created_at) : "-";

      case "status":
        return renderStatus(item.active ? "Active" : "Inactive");

      case "last login":
        // Assuming you have last_login data in your API response
        // If not, you'll need to add it to the data structure
        return item.last_login ? formatDateForRoles(item.last_login) : "-";

      case "actions":
        return renderEmployeesActions(item, rowIndex);

      default:
        return "-";
    }
  };

  // Function to render actions for employees type with three dots menu
  const renderEmployeesActions = (item, rowIndex) => {
    return (
      <div className="flex items-center gap-2">
        {/* View Profile Button */}
        <Link
          to={`/employees/employee-info/${item?.id}`}
          className="w-24 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition cursor-pointer"
        >
          View Profile
        </Link>

        {/* Three Dots Menu */}
        <div className="relative">
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, rowIndex)}
            className="w-8 h-8"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedRow === rowIndex}
            onClose={handleMenuClose}
            PaperProps={{
              style: {
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                minWidth: "120px",
              },
            }}
          >
            {/* Edit Menu Item */}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openModal("addEditEmployee", item?.id, "update");
              }}
              className="flex items-center gap-2 hover:bg-gray-50"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <img src="/img/edit.svg" alt={t("common.edit")} className="w-3 h-3" />
              </div>
              <span className="text-sm">{t("common.edit")}</span>
            </MenuItem>

            {/* Delete Menu Item */}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openModal("delete", item?.id);
              }}
              className="flex items-center gap-2 hover:bg-gray-50 text-red-600"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <img src="/img/delete.svg" alt={t("common.delete")} className="w-3 h-3" />
              </div>
              <span className="text-sm">{t("common.delete")}</span>
            </MenuItem>
          </Menu>
        </div>
      </div>
    );
  };

  // Function to render employees count (like in the photo: "5 employees hold this role")
  const renderEmployeesCount = (count) => {
    if (count === 0 || !count) return "No employees hold this role";
    return `${count} employee${count !== 1 ? "s" : ""} hold this role`;
  };

  // Function to render actions for roles type with three dots menu
  const renderRolesActions = (item, rowIndex) => {
    return (
      <div className="flex items-center gap-2">
        {/* View Details Button */}
        <button
          onClick={() => openModal("roleDetails", item?.id)}
          className="w-24 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition cursor-pointer"
        >{t("common.viewDetails")}</button>

        {/* Three Dots Menu */}
        <div className="relative">
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, rowIndex)}
            className="w-8 h-8"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedRow === rowIndex}
            onClose={handleMenuClose}
            PaperProps={{
              style: {
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                minWidth: "120px",
              },
            }}
          >
            {/* Edit Menu Item */}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openModal("addEditRole", item?.id);
              }}
              className="flex items-center gap-2 hover:bg-gray-50"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <img src="/img/edit.svg" alt={t("common.edit")} className="w-3 h-3" />
              </div>
              <span className="text-sm">{t("common.edit")}</span>
            </MenuItem>

            {/* Delete Menu Item */}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openModal("delete", item?.id);
              }}
              className="flex items-center gap-2 hover:bg-gray-50 text-red-600"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <img src="/img/delete.svg" alt={t("common.delete")} className="w-3 h-3" />
              </div>
              <span className="text-sm">{t("common.delete")}</span>
            </MenuItem>
          </Menu>
        </div>
      </div>
    );
  };

  // Function to render table cell content for roles type
  const renderRolesTableCell = (header, item, rowIndex) => {
    switch (header.toLowerCase()) {
      case "role id":
        return renderRoleId(item.id);

      case "role name":
        return item.name || "-";

      case "description":
        return item.note || "-";

      case "number of employees":
        return renderEmployeesCount(item.users_count || 0);

      case "created date":
        return item.created_at ? item.created_at.split("T")[0] : "-";

      case "last updated":
        return formatDateForRoles(item.updated_at);

      case "actions":
        return renderRolesActions(item, rowIndex);

      default:
        return "-";
    }
  };

  // Function to render actions for car requests
  const renderCarRequestActions = (item) => {
    const { status_label, car_verification_request_id } = item;

    // If status is "Under Review" or "pending", show approve/reject buttons
    if (status_label === "Under Review") {
      return (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => openModal("rejectUser", item?.car_id)}
            className="w-20 h-8 rounded-[50px] bg-bg10 text-white flex items-center justify-center text-sm font-medium transition  cursor-pointer"
          >
            reject
          </button>
          <button
            onClick={() =>
              openModal(
                "acceptCarRequest",
                item?.car_verification_request_id || item?.id || item?.car_id,
              )
            }
            className="w-20 h-8 rounded-[50px] bg-primary text-white flex items-center justify-center text-sm font-medium transition cursor-pointer"
          >
            approve
          </button>
        </div>
      );
    }
  };

  // Function to render table cell content for carRequest type
  const renderCarRequestTableCell = (header, item) => {
    switch (header) {
      case "Vehicle ID":
        return item.vehicle_id || "-";

      case "Vehicle Picture":
        return (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() =>
                openImagePreview(item.vehicle_picture, item.vehicle_name)
              }
              disabled={!item.vehicle_picture}
              className="disabled:cursor-default"
              style={{ lineHeight: 0 }}
            >
              <VehiclePictureCell
                imageUrl={item.vehicle_picture}
                alt={item.vehicle_name}
                className="w-12 h-12"
              />
            </button>
          </div>
        );

      case "Vehicle Name":
        return item.vehicle_name || "-";

      case "Model":
        return item.model || "-";

      case "Color":
        return item.color || "-";

      case "License Plate":
        return (
          <span className="font-semibold text-gray-800 text-center block">
            {item.license_plate || "-"}
          </span>
        );

      case "Driver":
        return item.driver || "-";

      case "fuel type":
        return item.fuel_type;

      case "seats number":
        return item.seats_number || "-";

      case "actions":
        return renderCarRequestActions(item);

      default:
        return "-";
    }
  };

  // Function to render status badge for vehicles (cars type)
  const renderVehicleStatusBadge = (statusData) => {
    if (!statusData) return "-";

    const { label, color } = statusData;
    const colorMap = {
      green: "#2ECC71",
      blue: "#3498DB",
      red: "#E74C3C",
      orange: "#F39C12",
      gold: "#F39C12",
      gray: "#95A5A6",
    };

    return (
      <span
        style={{
          color: colorMap[color] || "#000",
          fontWeight: 600,
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "14px",
          backgroundColor: `${colorMap[color]}15`, // 15% opacity of the color
        }}
      >
        {label}
      </span>
    );
  };

  // Function to render inspection button for vehicles
  const renderVehicleInspectionButton = (inspectionData, id) => {
    if (!inspectionData) return "-";

    const { action } = inspectionData;

    if (action === "Add Inspection") {
      return (
        <div className="flex items-center gap-3">
          <button
            onClick={() => openModal("addIncaption", id, "add")}
            className="w-28 h-8 rounded-[50px] bg-bg/50  flex items-center justify-center text-sm font-medium  transition mx-auto cursor-pointer"
          >
            Add Inspection
          </button>
          <Link
            to={`/vehicles/vehicle-info/${id}`}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-bg/50!"
          >
            <img src="/img/arrow-right3.svg" alt="" />
          </Link>
        </div>
      );
    } else if (action === "View Inspection") {
      return (
        <div className="flex items-center gap-3">
          <button
            onClick={() => openModal("incaption", id, "update")}
            className="w-28 h-8 rounded-[50px] bg-gray-200 text-gray-700 flex items-center justify-center text-sm font-medium hover:bg-gray-300 transition mx-auto cursor-pointer"
          >
            View Inspection
          </button>
          <Link
            to={`/vehicles/vehicle-info/${id}`}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-bg/50!"
          >
            <img src="/img/arrow-right3.svg" alt="" />
          </Link>
        </div>
      );
    }
    return "-";
  };

  // Function to render table cell content for cars type
  const renderCarTableCell = (header, item) => {
    switch (header) {
      case "Vehicle ID":
        return `#${item.vehicle_id || "-"}`;

      case "Vehicle Picture":
        return (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() =>
                openImagePreview(item.vehicle_picture, item.vehicle_name)
              }
              disabled={!item.vehicle_picture}
              className="disabled:cursor-default"
              style={{ lineHeight: 0 }}
            >
              <VehiclePictureCell
                imageUrl={item.vehicle_picture}
                alt={item.vehicle_name}
                className="w-12 h-12"
              />
            </button>
          </div>
        );

      case "Vehicle Name":
        return item.vehicle_name || "-";

      case "Model":
        return item.model || "-";

      case "Color":
        return item.color || "-";

      case "License Plate":
        return (
          <span className="font-semibold text-gray-800 text-center block">
            {item.license_plate || "-"}
          </span>
        );

      case "Driver":
        return item.driver || "-";

      case "Status":
        return (
          <div className="flex justify-center">
            {renderVehicleStatusBadge(item.status)}
          </div>
        );

      case "Current Version":
        return item?.versioning?.current_version ?? item?.current_version ?? "-";

      case "New Version":
        return item?.versioning?.new_version ?? item?.new_version ?? "-";

      case "Has Pending Edit":
        {
          const hasPending =
            item?.versioning?.has_pending_edit ??
            item?.has_pending_edit ??
            item?.has_pending;
        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              hasPending
                ? "bg-amber-100 text-amber-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {hasPending ? "Yes" : "No"}
          </span>
        );
        }

      case "Insurance":
        return (
          <div className="flex justify-center">
            {renderVehicleStatusBadge(item.insurance)}
          </div>
        );

      case "Technical Check":
        return (
          <div className="flex justify-center">
            {renderVehicleStatusBadge(item.technical_check)}
          </div>
        );

      case "Inspection":
        return (
          <div className="flex justify-center">
            {renderVehicleInspectionButton(item?.inspection, item?.vehicle_id)}
          </div>
        );

      default:
        return "-";
    }
  };

  // Function to render actions for numbers type
  const renderNumbersActions = (item, rowIndex) => {
    return (
      <div className="flex items-center gap-2">
        {/* Edit Button */}
        <button
          size="small"
          onClick={() => openModal("emergencyNumber", item?.id)}
          className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center cursor-pointer"
          title={t("common.edit")}
        >
          <img src="/img/edit.svg" alt="" />
        </button>
        {/* Delete Button */}
        <button
          size="small"
          onClick={() => handleDelete("number", item?.id)}
          className="w-7 h-7 rounded-full bg-bg/70 flex items-center justify-center cursor-pointer"
          title={t("common.delete")}
        >
          <img src="/img/delete.svg" alt="" />
        </button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && selectedRow === rowIndex}
          onClose={handleMenuClose}
          PaperProps={{
            style: {
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <MenuItem onClick={handleMenuClose}>
            <button
              onClick={() => openModal("view", item.id)}
              className="flex items-center gap-2 w-full h-full"
            >{t("common.viewDetails")}</button>
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
            <button
              onClick={() => openModal("duplicate", item.id)}
              className="flex items-center gap-2 w-full h-full"
            >
              Duplicate
            </button>
          </MenuItem>
        </Menu>
      </div>
    );
  };

  // Function to render table cell content for numbers type
  const renderNumbersTableCell = (headerKey, value, item, rowIndex) => {
    switch (headerKey) {
      case "recordid":
        // Format as #-03850 style
        return `#-${String(item.id).padStart(5, "0")}`;

      case "contactname":
        // Show English name, fallback to Arabic
        return item.name_en || item.name_ar || "-";

      case "phonenumber":
        return item.phone || "-";

      case "description":
        // Show English description, fallback to Arabic
        return item.description_en || item.description_ar || "-";

      case "status":
        return renderStatus(item.active);

      case "lastupdated":
        return formatDate(item.updated_at);

      case "actions":
        return renderNumbersActions(item, rowIndex);

      default:
        return value;
    }
  };

  // Function to render table cell content for driverPayment type
  const renderDriverPaymentTableCell = (header, item) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "payment id":
        return (
          <span className="font-semibold text-gray-800">
            {item.payment_id || "-"}
          </span>
        );

      case "driver name":
        return <span className="text-gray-700">{item.driver_name || "-"}</span>;

      case "amount (syp)":
        return (
          <span className="font-semibold text-gray-800">
            {item.amount_formatted
              ? `${item.amount_formatted} SYP`
              : `${item.amount || 0} SYP`}
          </span>
        );

      case "due date":
        return (
          <span className="text-gray-700">
            {item.due_date ? formatDateForDriverPayments(item.due_date) : "---"}
          </span>
        );

      case "payment date":
        return (
          <span className="text-gray-700">
            {item.payment_date
              ? formatDateForDriverPayments(item.payment_date)
              : "---"}
          </span>
        );

      case "status":
        return renderDriverPaymentStatus(item);

      case "action":
        return renderDriverPaymentAction(item);

      default:
        return "-";
    }
  };

  // Function to render table cell content for withdrawals type
  const renderWithdrawalsTableCell = (header, item) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "id":
        return <span className="font-semibold text-gray-800">#{item.id || "-"}</span>;
      case "driver":
      case "driver name":
        return item.driver_name || "-";
      case "phone":
      case "driver phone":
        return item.driver_phone || "-";
      case "amount":
        return item.amount ?? "-";
      case "currency":
        return item.currency || item.currency_id || "-";
      case "receipt image":
      case "receipt image url":
        return item.receipt_image_url ? (
          <button
            type="button"
            onClick={() =>
              openImagePreview(item.receipt_image_url, `Receipt ${item.id || ""}`)
            }
            className="w-20 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition cursor-pointer"
          >
            {t("admin.view")}
          </button>
        ) : (
          "-"
        );
      case "status":
        return renderStatus(item.status_label || item.status);
      case "office reference":
      case "office reference number":
        return item.office_reference_no || "-";
      case "created at":
        return item.created_at ? formatDate(item.created_at) : "-";
      default:
        return "-";
    }
  };

  // Function to render status badge for driver payments
  const renderDriverPaymentStatus = (item) => {
    const status = item.status || "";
    const statusColor = item.status_color || "gray";
    const statusEnum = item.status_enum || "";

    // Map status colors
    const colorMap = {
      green: "#2ECC71", // Paid
      red: "#E74C3C", // Overdue
      orange: "#F39C12", // Unpaid
      gray: "#95A5A6", // Other
    };

    const color = colorMap[statusColor] || "#95A5A6";

    return (
      <span
        style={{
          color: color,
          fontWeight: 600,
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "14px",
          backgroundColor: `${color}15`, // 15% opacity
        }}
      >
        {status}
      </span>
    );
  };

  // Function to render table cell content for adminLogs type
  const renderAdminLogsTableCell = (header, item) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "action id":
        return `#${item.id}`;

      case "action type":
        return renderLogsOperation(item.action);

      case "action title":
        return renderLogsTitle(item.entity, item.action);

      case "action description":
        return renderLogsDescription(item.entity, item.action, item);

      case "action section":
        // Capitalize the entity name for section
        return (
          item.entity?.charAt(0)?.toUpperCase() + item.entity?.slice(1) || "-"
        );

      case "date & time":
        return renderLogsDateTime(item.created_at);

      case "severity level":
        const severity = renderLogsSeverity(item.action, item.entity);
        return renderStatus(severity);

      case "action result":
        return renderLogsResult(item.success);

      case "actions":
        return (
          <button
            className="w-24 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition cursor-pointer mx-auto"
            onClick={() => openModal("actionDetails", item.id)}
          >{t("common.viewDetails")}</button>
        );

      default:
        return "-";
    }
  };

  // Function to render action button for driver payments
  const renderDriverPaymentAction = (item) => {
    const actionCompleted = item.action_completed || false;
    const statusEnum = item.status_enum || "";

    if (actionCompleted || statusEnum === "completed") {
      // Completed action with checkmark
      return (
        <div className="flex items-center justify-center gap-2">
          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
            <span className="text-green-600 font-bold text-lg">✔</span>
          </div>
          <span className="text-green-600 font-medium">Completed</span>
        </div>
      );
    }
  };

  // Function to render actions for disputes with three dots menu
  const renderDisputeActions = (item, rowIndex) => {
    const isPending = item?.status?.toLowerCase() === "pending";

    return (
      <div className="flex items-center gap-2">
        {/* View Details Button */}
        <Link
          to={`/disputes/details/${item?.id}`}
          className="w-24 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition cursor-pointer"
        >{t("common.viewDetails")}</Link>

        {/* Three Dots Menu - Only show for pending disputes */}
        {isPending && (
          <div className="relative">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, rowIndex);
              }}
              className="w-8 h-8"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && selectedRow === rowIndex}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  minWidth: "120px",
                },
              }}
            >
              {/* Approve Menu Item */}
              <MenuItem
                onClick={() => {
                  // Set type first, then open modal after a small delay to ensure state update
                  setType("approve");
                  handleMenuClose();
                  // Use setTimeout to ensure React state updates before opening modal
                  setTimeout(() => {
                    openModal("confirm", item?.id);
                  }, 50);
                }}
                className="flex items-center gap-2 hover:bg-gray-50 text-green-600"
              >
                <div className="w-4 h-4 flex items-center justify-center"></div>
                <span className="text-sm text-primary">{t("admin.approve")}</span>
              </MenuItem>

              {/* Reject Menu Item */}
              <MenuItem
                onClick={() => {
                  // Set type first, then open modal after a small delay to ensure state update
                  setType("reject");
                  handleMenuClose();
                  // Use setTimeout to ensure React state updates before opening modal
                  setTimeout(() => {
                    openModal("confirm", item?.id);
                  }, 50);
                }}
                className="flex items-center gap-2 hover:bg-gray-50 text-red-600"
              >
                <div className="w-4 h-4 flex items-center justify-center"></div>
                <span className="text-sm text-bg10">{t("admin.reject")}</span>
              </MenuItem>
            </Menu>
          </div>
        )}
      </div>
    );
  };

  // Function to render table cell content for disputes type
  const renderDisputeTableCell = (header, item, rowIndex) => {
    const headerLower = header.toLowerCase();

    switch (headerLower) {
      case "dispute id":
        return `#${item.id}`;

      case "complainant":
        return item.complainant_name || "-";

      case "against":
        return item.against_name || "-";

      case "trip id":
        return `#${item.trip_id}`;

      case "created at":
        return formatDateForDisputes(item.created_at);

      case "assigned to":
        return item.assigned_to_name || "-";

      case "type":
        return item.type?.name_en || item.type?.name_ar || "-";

      case "status":
        return renderStatus(item.status);

      case "priority":
        return item.priority || "-";

      case "actions":
        return renderDisputeActions(item, rowIndex);

      default:
        return "-";
    }
  };

  // Update the renderTableCell function to handle the specific headers:
  const renderTableCell = (header, item, rowIndex) => {
    // Normalize the header key
    const headerKey = header.toLowerCase().replace(/\s+/g, "");

    // Map header names to actual data keys from your API
    const keyMap = {
      // Support data keys - ADDED
      actionid: "id",
      "action id": "id",
      photo: "title",
      name: "title",
      phone: "phone",
      createdat: "created_at",
      "created at": "created_at",
      lastupdate: "updated_at",
      "last update": "updated_at",
      type: "category",
      status: "status",
      channel: "channel",
      actions: "actions",

      // Accounts data keys
      userid: "user_id",
      profilepicture: "profile_picture",
      fullname: "full_name",
      phonenumber: "phone_number",
      usertype: "user_type",
      accountstatus: "account_status",
      accountstatuscolor: "account_status_color",
      lastactive: "last_active",
      registrationdate: "registration_date",
      actions: "actions",

      // Trips data keys - UPDATED FOR YOUR HEADERS
      tripid: "id",
      driver: "driver",
      triptype: "trip_type",
      route: "route", // This will be created from start_point and end_point

      // Handle "Start Date / Time" and "End Date / Time" headers
      startdatetime: "start_time", // For "Start Date / Time" header
      enddatetime: "end_time", // For "End Date / Time" header

      // Alternative mappings if needed
      "startdate/time": "start_time",
      "enddate/time": "end_time",

      status: "status",
      price: "price_per_seat",

      // Numbers data keys
      recordid: "id",
      contactname: "name_en",
      description: "description_en",
      lastupdated: "updated_at",

      // Vehicles data keys
      vehicleid: "vehicle_id",
      vehiclepicture: "vehicle_picture",
      vehiclename: "vehicle_name",
      licenseplate: "license_plate",
      technicalcheck: "technical_check",

      // Car request data keys
      carid: "car_id",
      carverificationrequestid: "car_verification_request_id",
      fuelttype: "fuel_type",
      seatsnumber: "seats_number",
      statuslabel: "status_label",

      // Roles data keys
      roleid: "id",
      rolename: "name",

      numberofemployees: "users_count",
      createddate: "created_at",

      // Employees data keys - ADD THIS
      employee: "id",
      employeephoto: "photo_url",

      employeerole: "role",
      joineddate: "created_at",

      lastlogin: "last_login",
      amount: "amount",
      transaction_id: "transaction_id",
      wallet_name: "wallet_name",
      owner: "owner",
      current_balance: "current_balance",

      // Driver payment data keys - ADDED
      paymentid: "payment_id",
      payment_id: "payment_id",
      drivername: "driver_name",
      driver_name: "driver_name",
      "amount(syp)": "amount",
      amount_formatted: "amount_formatted",
      duedate: "due_date",
      due_date: "due_date",
      paymentdate: "payment_date",
      payment_date: "payment_date",
      status_enum: "status_enum",
      status_color: "status_color",
      action_completed: "action_completed",

      // Admin logs data keys - ADDED
      actionid: "id",
      actiontype: "action",
      actiontitle: "entity",
      actiondescription: "description",
      actionsection: "entity",
      datetime: "created_at",
      severitylevel: "severity",
      actionresult: "success",

      // OCCASIONS DATA KEYS - ADDED
      "name(ar)": "name_ar",
      name_ar: "name_ar",
      "name(en)": "name_en",
      name_en: "name_en",
      date: "date",
      "points/user": "points_per_user",
      points_per_user: "points_per_user",

      executedat: "executed_at",
      executed_at: "executed_at",
      executed_by: "executed_by",
      target_all: "target_all",
      target_roles: "target_roles",
      target_genders: "target_genders",

      // REWARDS DATA KEYS - ADDED
      image: "image_path",

      type: "type",
      requiredpoints: "required_points",
      required_points: "required_points",
      active: "active",

      // DISPUTES DATA KEYS - ADDED
      disputeid: "id",
      "dispute id": "id",
      complainant: "complainant_name",
      complainant_name: "complainant_name",
      against: "against_name",
      against_name: "against_name",
      "trip id": "trip_id",
      createdat: "created_at",
      "created at": "created_at",
      assignedto: "assigned_to_name",
      "assigned to": "assigned_to_name",

      priority: "priority",
    };

    const dataKey = keyMap[headerKey] || headerKey;
    const value = item[dataKey] || "";

    // Handle different types of data
    switch (type) {
      case "support": // ADDED CASE
        return renderSupportTableCell(header, item, rowIndex);
      case "currencies":
        return renderCurrenciesTableCell(header, item, rowIndex);
      case "models":
        return renderModelsTableCell(header, item, rowIndex);
      case "companies":
        return renderCompaniesTableCell(header, item, rowIndex);
      case "colors": // Add this
        return renderColorsTableCell(header, item, rowIndex);
      case "fuelType": // Add this
        return renderFuelTypeTableCell(header, item, rowIndex);
      case "insurance": // Add this
        return renderInsuranceTableCell(header, item, rowIndex);
      case "services": // Add this
        return renderServicesTableCell(header, item, rowIndex);
      case "categories":
        return renderCategoriesTableCell(header, item, rowIndex);
      case "reports":
        return renderReportsTableCell(header, item, rowIndex);
      case "incidenc": // ADD THIS CASE
        return renderIncidencTableCell(header, item, rowIndex);
      case "distputs": // ADD THIS CASE
        return renderDisputeTableCell(header, item, rowIndex);
      case "rewardsTransactions": // ADD THIS LINE
        return renderRewardsTransactionsTableCell(header, item);
      case "rewards": // ADD THIS CASE
        return renderRewardsTableCell(header, item, rowIndex);
      case "occations":
        return renderOccasionsTableCell(header, item, rowIndex);
      case "appVersions":
        return renderAppVersionsTableCell(header, item);
      case "carRequest":
        return renderCarRequestTableCell(header, item);

      case "cars":
        return renderCarTableCell(header, item);

      case "profileTrips":
      case "trips":
        return renderTripTableCell(headerKey, value, item);

      case "numbers":
        return renderNumbersTableCell(headerKey, value, item, rowIndex);

      case "roles":
        return renderRolesTableCell(header, item, rowIndex);

      case "employees": // ADD THIS CASE
        return renderEmployeesTableCell(header, item, rowIndex);
      case "transactions":
        return renderTransactionTableCell(header, item);
      case "wallets":
        return renderWalletsTableCell(header, item);
      case "driverPayment": // ADD THIS CASE
        return renderDriverPaymentTableCell(header, item);
      case "withdrawals":
        return renderWithdrawalsTableCell(header, item);
      case "adminLogs": // ADD THIS CASE
        return renderAdminLogsTableCell(header, item);
      case "logs": // NEW CASE for logs type
        return renderLogsTableCell(header, item);
      case "accounts":
      default:
        // Safety check to prevent rendering objects directly
        if (typeof value === "object" && value !== null) {
          return JSON.stringify(value);
        }
        return renderDefaultTableCell(headerKey, value, item, rowIndex);
    }
  };

  // Also update the renderTripTableCell function to handle the specific header keys:
  const renderTripTableCell = (headerKey, value, item) => {
    // Helper to format trip type
    const formatTripType = (tripType) => {
      if (!tripType) return "-";
      // Map API trip types to display values
      const typeMap = {
        both: "Both",
        passengers: "Passengers",
        passenger: "Passenger",
        cargo: "Cargo",
      };
      return (
        typeMap[tripType] ||
        tripType.charAt(0).toUpperCase() + tripType.slice(1)
      );
    };

    // Helper to map status
    const mapStatus = (status) => {
      const statusMap = {
        on_started: "onStarted",
        on_going: "onGoing",
        completed: "Completed",
        current_balance: "CurrentBalance",
        cancelled: "Canceled",
        pending: "Pending",
      };
      return statusMap[status] || status;
    };

    // Helper to format price
    const formatPrice = (price) => {
      if (!price) return "0 SYP";
      const priceNum = parseFloat(price);
      // Format with commas and no decimals
      const formattedPrice = priceNum.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return `${formattedPrice} SYP`;
    };

    // Handle different header variations
    switch (headerKey) {
      case "tripid":
      case "trip_id":
        // Format as #9
        return `#${item.id || item.trip_id || "-"}`;

      case "driver":
        // You need driver data in your API response
        // For now, return placeholder or check for driver field
        return item?.driver?.name || "-";

      case "triptype":
      case "trip type":
        return formatTripType(item.trip_type);
      case "triproute":
        return <span>{item?.trip_route}</span>;
      case "route":
        return (
          <span>
            {item?.start_point?.name_en || item?.start_point?.name_ar || "N/A"}{" "}
            -&gt;{" "}
            {item?.end_point?.name_en || item?.end_point?.name_ar || "N/A"}
          </span>
        );

      case "starttime": // Handle "Start Date / Time" header
      case "startdatetime":
      case "startdate/time":
        // SIMPLIFIED: Show date and time separately
        return (
          <div className="flex flex-col">
            <span>{item.date || "-"}</span>
            <span>{item.start_time || "-"}</span>
          </div>
        );

      case "endtime": // Handle "End Date / Time" header
      case "enddatetime":
      case "enddate/time":
        // SIMPLIFIED: Show date and time separately
        return (
          <div className="flex flex-col">
            <span>{item.date || "-"}</span>
            <span>{item.end_time || "-"}</span>
          </div>
        );

      case "vehicletype":
        return <span>{item?.vehicle_type}</span>;

      case "status":
        const displayStatus = mapStatus(item?.status);
        return (
          <div className="flex justify-center">
            {renderStatus(displayStatus)}
          </div>
        );

      case "price":
        return formatPrice(item?.price_per_seat);

      case "fare":
        return formatPrice(item.fare);

      case "actions":
        return (
          <Link
            to={`/trips/trip-info/${item?.id}`}
            className="w-24 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition mx-auto cursor-pointer"
          >{t("common.viewDetails")}</Link>
        );

      default:
        // Safety check
        if (typeof value === "object" && value !== null) {
          return JSON.stringify(value);
        }
        return value || "-";
    }
  };

  // Render table cell for default/accounts data
  const renderDefaultTableCell = (headerKey, value, item, rowIndex) => {
    // Safety check to prevent rendering objects directly
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value);
    }

    // Check if user is already blocked
    const isBlocked = item.is_banned;

    switch (headerKey) {
      case "userid":
        return `#${value}`;

      case "profilepicture":
        return (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() => openImagePreview(value, item.full_name)}
              disabled={!value}
              className="disabled:cursor-default"
              style={{ lineHeight: 0 }}
            >
              <ProfilePictureCell
                imageUrl={value}
                fullName={item.full_name}
                className="w-8 h-8"
              />
            </button>
          </div>
        );

      case "fullname":
        return value;

      case "phonenumber":
        return value;

      case "usertype":
        if (!value) return "";

        const formattedUserType = value.charAt(0).toUpperCase() + value.slice(1);
        const formattedDriverType = item?.driver_type
          ? item.driver_type.charAt(0).toUpperCase() + item.driver_type.slice(1)
          : null;

        if (value === "driver" && formattedDriverType) {
          return `${formattedUserType} - ${formattedDriverType}`;
        }

        return formattedUserType;

      case "accountstatus":
        return renderStatus(value, item.account_status_color);

      case "lastactive":
        return value ? value : "-";

      case "registrationdate":
        return value;

      case "actions":
        return (
          <div className="flex items-center gap-2">
            {type === "accounts" && (
              <Link
                to={`/accounts/profile/${item?.user_id}/${item?.user_type}`}
                className="w-24 h-8 rounded-[50px] bg-fifth flex items-center justify-center text-sm font-medium hover:bg-fifth/80 transition"
              >
                View Profile
              </Link>
            )}

            {/* Three Dots IconButton with Menu */}
            <div className="relative">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e, rowIndex);
                }}
                className="w-8 h-8 hover:bg-gray-100"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && selectedRow === rowIndex}
                onClose={handleMenuClose}
                PaperProps={{
                  style: {
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    minWidth: "180px",
                  },
                }}
              >
                {/* Driver with "Active" status - Show Edit button */}
                {item.user_type === "driver" &&
                  item.account_status === "Active" && (
                    <MenuItem
                      component={Link}
                      to={`/accounts/create-account/${item?.user_id}/edit`}
                      onClick={() => {
                        handleMenuClose();
                        openModal("editDriver", item?.user_id);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "14px",
                        py: 1,
                        color: "primary.main",
                      }}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <img
                          src="/img/edit.svg"
                          alt={t("common.edit")}
                          className="w-3 h-3"
                        />
                      </div>
                      <span>{t("common.edit")}</span>
                    </MenuItem>
                  )}

                {/* Common actions for all users, when not blocked */}
                {!isBlocked && (
                    <>
                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          openModal("tempBan", item?.user_id);
                          setBanType("temporary");
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: "14px",
                          py: 1,
                        }}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <img
                            src="/img/clock.svg"
                            alt="Temporary Ban"
                            className="w-3 h-3"
                          />
                        </div>
                        <span>Ban Temporarily</span>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          openModal("permBan", item?.user_id);
                          setBanType("permanent");
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: "14px",
                          py: 1,
                        }}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <img
                            src="/img/ban.svg"
                            alt="Permanent Ban"
                            className="w-3 h-3"
                          />
                        </div>
                        <span>Ban Permanently</span>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          openModal("delete", item?.user_id);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: "14px",
                          py: 1,
                          color: "error.main",
                        }}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <img
                            src="/img/delete.svg"
                            alt={t("common.delete")}
                            className="w-3 h-3"
                          />
                          
                        </div>
                        <span>{t("common.delete")}</span>
                      </MenuItem>
                    </>
                  )}

                {/* Show Unban option if user is already blocked */}
                {isBlocked && (
                  <>
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        openModal("confirm", item?.user_id);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "14px",
                        py: 1,
                        color: "success.main",
                      }}
                    >
                      <span>Unban User</span>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        openModal("delete", item?.user_id);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "14px",
                        py: 1,
                        color: "success.main",
                      }}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <img
                          src="/img/delete.svg"
                          alt="Unban"
                          className="w-3 h-3"
                        />
                      </div>
                      <span>{t("common.delete")}</span>
                    </MenuItem>
                  </>
                )}
              </Menu>
            </div>
          </div>
        );
      default:
        return value;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state - Use ErrorHandler component
  if (isError) {
    // Extract error details
    const errorStatus =
      error?.status || error?.response?.status || error?.code || 500;
    const errorMessage =
      error?.message || error?.data?.message || `Error loading ${type} data`;

    // Skip 401 errors (they should be handled by parent)
    if (errorStatus === 401) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={4}
          minHeight="300px"
        >
          <div className="flex flex-col items-center gap-4">
            <div className="text-amber-500 text-5xl mb-2">🔒</div>
            <Typography
              variant="h6"
              color="textPrimary"
              className="font-semibold"
            >
              Session Expired
            </Typography>
            <Typography color="textSecondary" className="text-center max-w-md">
              Your session has expired. Please login again.
            </Typography>
          </div>
        </Box>
      );
    }

    return (
      <div className="w-full py-8 px-4">
        <ErrorHandler
          statusCode={errorStatus}
          message={errorMessage}
          onRetry={onRetry || (() => window.location.reload())}
          onGoBack={() => window.history.back()}
          showDetails={process.env.NODE_ENV === "development"}
          errorDetails={{
            status: errorStatus,
            message: errorMessage,
            type: type,
            timestamp: new Date().toISOString(),
            endpoint: window.location.pathname,
            ...(error?.data || {}),
          }}
        />
      </div>
    );
  }

  // Empty state
  if (!tableData || tableData.length === 0) {
    return (
      <div className="w-full py-8 px-4">
        <ErrorHandler
          statusCode={204} // No Content
          message={`No ${type} data available`}
          onRetry={onRetry}
          showDetails={false}
        />
      </div>
    );
  }

  const shouldUseStickyHeader = Boolean(maxHeight);

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          ...tableContainerStyle,
          px: 2,
          pb: 2,
          maxHeight: maxHeight || undefined,
          overflowY: maxHeight ? "auto" : undefined,
          overflowX: "auto",
          border: "1px solid #EEF2F7",
          backgroundColor: "#fff",
        }}
      >
        <Table
          stickyHeader={shouldUseStickyHeader}
          sx={{
            minWidth: 1000,
            "& th, & td": {
              padding: "8px 4px", // Reduced padding
              fontSize: "13px", // Slightly smaller font
            },
            [`${theme.breakpoints.down("md")}`]: {
              minWidth: 800, // Smaller min width for mobile
              "& th, & td": {
                fontSize: "11px", // Even smaller for mobile
                padding: "4px 2px",
              },
            },
          }}
        >
          <TableHead>
            <TableRow className="bg-sixth">
              {tableHeader.map((head, i) => (
                <TableCell
                  key={i}
                  sx={{
                    ...(shouldUseStickyHeader
                      ? {
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                        }
                      : {}),
                    fontWeight: 600,
                    fontSize: 13, // Reduced from 14
                    color: "#374151",
                    backgroundColor: "#F9FAFB",
                    padding: "8px 4px", // Add this
                    textAlign:
                      (type === "cars" ||
                        type === "carRequest" ||
                        type === "driverPayment" ||
                        type === "logs" ||
                        type === "occasions" ||
                        type === "appVersions" ||
                        type === "rewards" ||
                        type === "disputs" ||
                        type === "reports" ||
                        type === "support") &&
                      i > 0
                        ? "center"
                        : "left",
                  }}
                >
                  {translateHeader(head)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {tableData?.map((item, rowIndex) => (
              <TableRow
                key={
                  item.id ||
                  item.trip_id ||
                  item.user_id ||
                  item.vehicle_id ||
                  item.car_verification_request_id ||
                  item.payment_id ||
                  rowIndex
                }
                sx={{
                  ...tableRowStyle,
                  "&:hover": {
                    backgroundColor: "#F9FAFB",
                  },
                }}
              >
                {tableHeader.map((header, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    sx={{
                      borderBottom: "1px solid #E5E7EB",
                      color: "#374151",
                      padding: "8px 4px",
                      textAlign:
                        (type === "cars" ||
                          type === "carRequest" ||
                          type === "driverPayment" ||
                          type === "logs" ||
                          type === "occasions" ||
                          type === "appVersions" ||
                          type === "rewards" ||
                          type === "disputs" ||
                          type === "reports" ||
                          type === "support") &&
                        cellIndex > 0
                          ? "center"
                          : "left",
                    }}
                  >
                    {renderTableCell(header, item, rowIndex)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={imagePreviewOpen}
        onClose={closeImagePreview}
        maxWidth="sm"
        fullWidth
      >
        <div className="flex items-center justify-between px-4 py-2">
          <div className="font-semibold truncate pr-2">{imagePreviewAlt}</div>
          <IconButton onClick={closeImagePreview} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <DialogContent sx={{ pt: 0 }}>
          <div className="flex items-center justify-center relative">
            {imagePreviewLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                <CircularProgress size={28} />
              </div>
            )}
            <AuthenticatedImage
              src={imagePreviewSrc}
              alt={imagePreviewAlt}
              className="max-h-[70vh] w-auto rounded-xl object-contain"
              onLoad={() => setImagePreviewLoading(false)}
              onError={() => setImagePreviewLoading(false)}
              fallback={
                <div className="w-full py-10 text-center text-gray-500">
                  No image
                </div>
              }
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
